import Btn from "@/components/Btn";
import { SUBMITTED } from "@/constants/area";
import { ASSIGNED, RELEASED, RELEASE_PENDING } from "@/constants/request";
import {
  ACCEPTED,
  OVERDUE,
  PARTIALLY_ACCEPTED,
  REASSIGNED,
  REJECTED,
  RELEASE_APPROVED,
  RELEASE_REJECTED,
} from "@/constants/tasks";
import { FullDrawerProps } from "@/interfaces/drawer.interface";
import { isTouchScreen } from "@/utils/common";
import { Button } from "@progress/kendo-react-buttons";
import { Drawer, DrawerContent } from "@progress/kendo-react-layout";
import { Check, ChevronLeft, Map, X } from "lucide-react";
import React, { useEffect, useState } from "react";

const BaseDrawer: React.FC<FullDrawerProps> = ({
  isOpen,
  onClose,
  showIcon = false,
  title,
  subtitle,
  children,
  className = "",
  primaryActionText = "",
  onPrimaryAction,
  primaryActionDisabled = false,
  secondaryActionText = "Cancel",
  onSecondaryAction = onClose,
  secondaryActionDisabled = false,
  showPrimaryAction = true,
  showSecondaryAction = true,
  maxW = "35vw",
  status,
  showBackButton = false,
  onBackButtonClick,
  showTertiaryAction = false,
  tertiaryActionText = "",
  tertiaryActionDisabled = false,
  onTertiaryAction,
  // … existing props …
  showCustomAction = false,
  customActionText = "",
  customActionDisabled = false,
  onCustomAction,
}) => {
  const getTextColor = () => {
    var textColor = "#A1A1A1"; // Default color
    switch (status) {
      case SUBMITTED:
      case ASSIGNED:
      case REASSIGNED:
        textColor = "#FF9800";
        break;
      case OVERDUE:
        textColor = "#D32F2F";
        break;
      case RELEASED:
        textColor = "#219653";
        break;
      case RELEASE_PENDING:
        textColor = "#219653";
        break;
      case RELEASE_APPROVED:
        textColor = "#0095ff";
        break;
      case PARTIALLY_ACCEPTED:
      case ACCEPTED:
        textColor = "#219653";
        break;
      case RELEASE_REJECTED:
      case REJECTED:
        textColor = "#EB5757";
        break;
    }

    return textColor;
  };

  const [drawerWidth, setDrawerWidth] = useState(maxW);

  // Calculate and set drawer width based on orientation
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isLandscape = width > height;

      // Use different widths based on device size and orientation
      if (width <= 768 || width <= 1024 || isTouchScreen()) {
        // Mobile
        // Tablet
        setDrawerWidth("100vw");
      } else {
        // Desktop
        setDrawerWidth(maxW);
      }
    };

    // Initialize
    handleResize();

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [maxW]);

  return (
    <Drawer
      expanded={isOpen}
      position="start"
      mode="overlay"
      animation={{ duration: 400 }}
      className={`customDrawer transition-all duration-500 ${className}`}
    >
      <DrawerContent>
        <div
          className="fixed inset-0 bg-black bg-opacity-50 touch-none"
          style={{
            touchAction: "none",
            pointerEvents: isOpen ? "auto" : "none",
            opacity: isOpen ? 1 : 0,
            transition: "opacity 400ms ease",
          }}
          onClick={onClose}
        ></div>
        <div
          className={`flex z-[99999] touch-none flex-col bg-white fixed right-0 top-0 bottom-0 h-full w-full transform transition-all duration-500 overflow-hidden ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          style={{
            width: drawerWidth,
            maxWidth: drawerWidth,
          }}
        >
          {/* Drawer Header */}
          <div className="card">
            <div className="py-3 px-6 w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {showBackButton && (
                    <Button
                      fillMode="flat"
                      className="p-1 mr-2 bg-none outline-none"
                      onClick={onBackButtonClick || onClose}
                    >
                      <ChevronLeft className="w-[20px] h-[20px] text-[#707070]" />
                    </Button>
                  )}
                  <div>
                    <h2 className="font-medium text-lg items-center flex gap-x-2">
                      {showIcon && <Map className="w-5 h-5" />}
                      {title}
                    </h2>
                    {subtitle && (
                      <p className="text-sm text-primary">{subtitle}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  {status && (
                    <div
                      className={`self-center mr-5 border border-[${getTextColor()}] rounded-md p-2 text-[15px] text-[${getTextColor()}]`}
                    >
                      {status}
                    </div>
                  )}
                  <Button
                    fillMode="flat"
                    className="p-2 bg-none outline-none"
                    onClick={onClose}
                  >
                    <X className="w-[26px] h-[26px] text-[#A1A1A1]" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 py-4 px-6 mt-2 overflow-y-auto overflow-x-hidden">
            {children}
          </div>

          {/* Drawer Footer with Actions */}
          {(primaryActionText || secondaryActionText || showCustomAction) && (
            <div className="flex justify-end gap-x-6 p-4 px-6 border-t">
              {/* CUSTOM ACTION (left side) */}
              {showCustomAction && customActionText && (
                <div className="w-[181px]">
                  <Btn
                    variant="secondary"
                    text={customActionText}
                    disabled={customActionDisabled}
                    onClick={onCustomAction || onClose}
                    icon={<Check className="w-[16px] h-[16px]" />}
                  />
                </div>
              )}
              {showSecondaryAction && secondaryActionText && (
                <div className="w-[181px]">
                  <Btn
                    className={`${
                      secondaryActionText === "Reject" ? "text-[#A1A1A1]" : ""
                    }`}
                    variant={`${
                      secondaryActionText === "Reject"
                        ? "danger"
                        : secondaryActionText === "Reassign"
                        ? "secondary"
                        : "tertiary"
                    }`}
                    text={secondaryActionText}
                    border
                    disabled={secondaryActionDisabled}
                    onClick={onSecondaryAction}
                    icon={
                      secondaryActionText === "Reassign" ? (
                        <Check className="w-[16px] h-[16px]" />
                      ) : (
                        <X className="w-[16px] h-[16px]" />
                      )
                    }
                  />
                </div>
              )}
              {showTertiaryAction && (
                <div className="w-[181px]">
                  <Btn
                    variant="secondary"
                    text={tertiaryActionText}
                    onClick={onTertiaryAction!}
                    border
                    disabled={tertiaryActionDisabled}
                    icon={<Check className="w-[16px] h-[16px]" />}
                  />
                </div>
              )}

              {primaryActionText && showPrimaryAction && (
                <div className="w-[181px]">
                  <Btn
                    variant="primary"
                    text={primaryActionText}
                    disabled={primaryActionDisabled}
                    onClick={onPrimaryAction!}
                    iconPosition="left"
                    icon={<Check className="w-[16px] h-[16px]" />}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default BaseDrawer;
